import { useState } from "react";
import { Calendar, MapPin, Ticket } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import { formatShowDate } from "@/data/site";

const STATUS_CLASS = {
  "Sold Out": "border-crimson text-red-300/80",
  Free: "border-emerald-700/60 text-emerald-300/80",
  Private: "border-slate-600 text-slate-300",
  Tickets: "border-amber-500/30 text-amber-300",
};

export const ShowRow = ({ show, past = false }) => {
  const d = formatShowDate(show.date);
  const status = show.status || "Tickets";
  const [lightbox, setLightbox] = useState(null);
  const photos = (show.photos || []).map((p) => ({ ...p, caption: `${show.title} \u25C8 ${d.long}` }));

  return (
    <article
      data-testid={`show-row-${show.id}`}
      className={`group grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center py-8 border-b border-amber-500/10 border-l-2 border-l-transparent hover:border-l-gold hover:bg-surface/40 px-4 sm:px-6 -mx-4 sm:-mx-6 transition-all duration-300 ${
        past ? "opacity-70 hover:opacity-100" : ""
      }`}
    >
      <div className="flex md:block items-center gap-4 md:text-center md:w-20">
        <span className={`font-mono text-3xl ${past ? "text-slate-400" : "text-amber-300"}`}>{d.day}</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
          {d.month} {"\u2019"}{d.year.slice(2)}
        </span>
      </div>
      <div className="min-w-0">
        <h2 className={`font-serif text-2xl sm:text-3xl tracking-tight ${past ? "text-slate-300 group-hover:text-amber-100" : "text-amber-100 group-hover:text-amber-50"}`}>
          {show.title}
        </h2>
        <p className="mt-2 text-sm text-slate-400 flex items-center gap-2">
          <MapPin size={14} className="text-amber-500/70 shrink-0" />
          {show.venue} {"\u2014"} {show.city}
        </p>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-2xl">{show.details}</p>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600 flex items-center gap-2">
          <Calendar size={12} /> {d.long} {"\u25C8"} {show.address}
        </p>
        {show.photos?.length > 0 && (
          <div data-testid={`show-photos-${show.id}`} className="mt-5 flex gap-3">
            {photos.map((p, i) => (
              <button
                key={p.src}
                type="button"
                onClick={() => setLightbox(i)}
                data-testid={`show-photo-link-${show.id}-${i}`}
                aria-label={`View photo: ${p.alt}`}
                className="group/photo relative block w-32 sm:w-40 aspect-[4/3] overflow-hidden border border-amber-500/15 hover:border-gold transition-colors duration-300"
              >
                <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover/photo:scale-105" />
              </button>
            ))}
          </div>
        )}
        <Lightbox items={photos} index={lightbox} onChange={setLightbox} onClose={() => setLightbox(null)} testId={`show-lightbox-${show.id}`} />
      </div>
      <div className="flex md:flex-col items-center md:items-end gap-3">
        {past ? (
          <span
            data-testid={`show-status-${show.id}`}
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-slate-700 text-slate-500"
          >
            Past
          </span>
        ) : (
          <>
            <span
              data-testid={`show-status-${show.id}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${STATUS_CLASS[status]}`}
            >
              {status}
            </span>
            {status !== "Private" && show.ticketUrl && (
              <a
                href={show.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`show-tickets-btn-${show.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold/10 border border-amber-500/30 text-amber-200 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-gold hover:text-midnight transition-all duration-300"
              >
                <Ticket size={13} /> {status === "Free" ? "RSVP" : "Tickets"}
              </a>
            )}
          </>
        )}
      </div>
    </article>
  );
};
